import React from 'react';

const QuestionTable = ({
    items,
    selectedTest,
    sectionName,
    handleButtonClick,
    questionIdField = 'questionIds',
    tagField = 'tag'
}) => {
    const formatCorrectAnswer = (questionObj, sectionName) => {
        if (sectionName === "Free Response") return "Too long";
        try {
            const parsed = JSON.parse(questionObj.correct_answer);
            const val = Array.isArray(parsed) ? parsed.join(", ") : questionObj.correct_answer;
            return val.length > 10 ? "—" : val;
        } catch {
            return questionObj.correct_answer?.length > 10 ? "—" : questionObj.correct_answer;
        }
    };

    return (
        <div className="mt-3 border border-slate-200 overflow-hidden">
            <div
                className="max-h-[480px] overflow-y-auto"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}
            >
                <table className="min-w-full">
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">#</th>
                            <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Section</th>
                            <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Answer</th>
                            <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Yours</th>
                            <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">View</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {items.map((questionObj, index) => (
                            <tr
                                key={index}
                                className={`transition-colors duration-100 ${
                                    questionObj.correct
                                        ? 'bg-white hover:bg-slate-50'
                                        : 'bg-rose-50/60 hover:bg-rose-50'
                                }`}
                            >
                                <td className="px-3 py-2.5">
                                    <span className={`text-xs font-bold ${
                                        questionObj.correct ? 'text-emerald-600' : 'text-rose-500'
                                    }`}>
                                        {index + 1}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5 text-xs text-slate-400 max-w-[80px] truncate">{sectionName}</td>
                                <td className="px-3 py-2.5 text-xs font-medium text-slate-700 capitalize">
                                    {formatCorrectAnswer(questionObj, sectionName)}
                                </td>
                                <td className="px-3 py-2.5 text-xs font-medium">
                                    {questionObj.selectedOption
                                        ? <span className="text-slate-700">{questionObj.selectedOption}</span>
                                        : <span className="text-slate-400 italic text-[11px]">Omitted</span>
                                    }
                                </td>
                                <td className="px-3 py-2.5">
                                    <button
                                        className="text-[11px] font-medium px-2.5 py-1 bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-100"
                                        onClick={() =>
                                            handleButtonClick(
                                                questionObj.selectedOption,
                                                questionObj[questionIdField],
                                                questionObj[tagField],
                                                selectedTest?.type,
                                                items,
                                                index
                                            )
                                        }
                                    >
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TabContent = ({
    items,
    correctItems,
    incorrectItems,
    selectedTest,
    sectionName,
    handleButtonClick,
    questionIdField,
    tagField
}) => {
    const total = items.length;
    const pct = total > 0 ? Math.round((correctItems / total) * 100) : 0;
    const barColor = pct >= 75 ? 'bg-emerald-400/80' : pct >= 50 ? 'bg-blue-400/80' : 'bg-rose-400/80';

    return (
        <div>
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-2 mt-3 mb-1">
                <div className="bg-slate-50 border border-slate-200 p-3.5 flex flex-col items-center">
                    <p className="text-2xl font-bold tabular-nums text-slate-800">{total}</p>
                    <p className="text-[10px] font-medium text-slate-400 mt-1 text-center leading-tight uppercase tracking-wider">Total</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 p-3.5 flex flex-col items-center">
                    <p className="text-2xl font-bold tabular-nums text-emerald-700">{correctItems}</p>
                    <p className="text-[10px] font-medium text-emerald-500/70 mt-1 text-center leading-tight uppercase tracking-wider">Correct</p>
                </div>
                <div className="bg-rose-50 border border-rose-100 p-3.5 flex flex-col items-center">
                    <p className="text-2xl font-bold tabular-nums text-rose-600">{incorrectItems}</p>
                    <p className="text-[10px] font-medium text-rose-400/70 mt-1 text-center leading-tight uppercase tracking-wider">Incorrect</p>
                </div>
            </div>

            {/* Accuracy bar */}
            <div className="mb-3 px-0.5">
                <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                    <span>Accuracy</span>
                    <span className="font-semibold tabular-nums text-slate-600">{pct}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 overflow-hidden">
                    <div className={`h-full ${barColor} transition-all duration-700`} style={{ width: `${pct}%` }} />
                </div>
            </div>

            <QuestionTable
                items={items}
                selectedTest={selectedTest}
                sectionName={sectionName}
                handleButtonClick={handleButtonClick}
                questionIdField={questionIdField}
                tagField={tagField}
            />
        </div>
    );
};

const TopicsTabContent = ({ missingTopics, selectedTest, handleButtonClick }) => {
    return (
        <div>
            <p className="text-base font-semibold text-slate-800 mt-3 mb-3">Missing Topics Overview</p>

            <div className="bg-slate-50 border border-slate-200 p-3.5 flex flex-col items-center mb-3">
                <p className="text-2xl font-bold tabular-nums text-slate-800">{missingTopics.length}</p>
                <p className="text-[10px] font-medium text-slate-400 mt-1 uppercase tracking-wider">Missing Questions</p>
            </div>

            <div className="border border-slate-200 overflow-hidden">
                <div
                    className="max-h-[450px] overflow-y-auto"
                    style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}
                >
                    <table className="min-w-full">
                        <thead className="sticky top-0 z-10">
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">#</th>
                                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Topic</th>
                                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">View</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {missingTopics.map((questionObj, index) => (
                                <tr
                                    key={index}
                                    className="bg-white hover:bg-slate-50 transition-colors duration-100"
                                >
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs font-semibold text-slate-500">{index + 1}</span>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs text-slate-700">{questionObj.topic}</td>
                                    <td className="px-3 py-2.5">
                                        <button
                                            className="text-[11px] font-medium px-2.5 py-1 bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-100"
                                            onClick={() =>
                                                handleButtonClick(
                                                    questionObj.selectedOption,
                                                    questionObj.questionIds,
                                                    selectedTest.test_name,
                                                    questionObj.tag
                                                )
                                            }
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export { TabContent, TopicsTabContent };
